import {Plain} from '../../../../shared/model/plain.type.model';
import {ConditionPageOption} from './conditions/condition-page-option.model';
import {DisplayOptions} from './conditions/display.options.model';
import {ElementPage} from './elements/element.page.model';
import {FormPage} from './form.page.model';
import {LABEL_KEY} from './label-key.model';
import {LabelTranslator} from './label-translator.model';
import {PageConclusion} from './page.conclusion.model';
import {resolveLabelKeys} from './resolve-label-keys.model';
import {QUESTION_TYPE} from './elements/question.type.model';
import {Stage} from './stage.model';
import {buildEmptySurveyInformation, SurveyInformation} from './survey.information.model';
import {SurveyType} from './survey.type.model';

/** Indicateurs de satisfaction : une enquête n'en mesure chacun qu'une fois. */
const UNIQUE_INDICATORS: QUESTION_TYPE[] = [QUESTION_TYPE.NPS, QUESTION_TYPE.CSAT, QUESTION_TYPE.CES];

/**
 * Indicateurs dont la présence ouvre la diffusion, par nature d'enquête.
 *
 * Une enquête de satisfaction est faite pour mesurer : diffusée sans indicateur, elle ne rapporterait
 * rien au tableau de bord. Une enquête par étapes se juge sur la recommandation ou la satisfaction —
 * le niveau d'effort s'y mesure à chaque étape et ne suffit pas à lui seul à la justifier.
 *
 * Un formulaire ne mesure rien : il n'attend aucun indicateur, n'importe quelle question le
 * justifie. `canPublish` le traite à part, c'est pourquoi rien n'est listé ici pour lui.
 */
const PUBLISHING_INDICATORS: Record<SurveyType, QUESTION_TYPE[]> = {
    [SurveyType.SATISFACTION]: [QUESTION_TYPE.CSAT, QUESTION_TYPE.CES, QUESTION_TYPE.NPS],
    [SurveyType.SATISFACTION_STAGES]: [QUESTION_TYPE.CSAT, QUESTION_TYPE.NPS],
    [SurveyType.FORM]: [],
};

/**
 * Ce que l'enquête doit mesurer, dit dans les mots de sa nature — c'est la phrase que l'utilisateur
 * lit quand la diffusion lui est refusée. Un formulaire ne mesure rien : ce qu'on lui demande, c'est
 * une question, n'importe laquelle, qui ne soit pas un simple commentaire.
 */
const MISSING_INDICATOR_HINTS: Record<SurveyType, string> = {
    [SurveyType.SATISFACTION]: 'label.publication_requires_satisfaction_indicator',
    [SurveyType.SATISFACTION_STAGES]: 'label.publication_requires_stages_indicator',
    [SurveyType.FORM]: 'label.publication_requires_question',
};

export class Survey {
    public id: number;
    public type: SurveyType;
    public information: SurveyInformation;
    public conclusionPage?: PageConclusion;
    public pages: FormPage[];
    public locale: string;

    constructor(data: Plain<Survey>) {
        this.id = data.id;
        this.type = data.type;
        // SurveyInformation est une simple structure de données : pas de réinstanciation nécessaire,
        // mais l'API l'omet sur un brouillon jamais paramétré.
        this.information = data.information ?? buildEmptySurveyInformation();
        this.conclusionPage = data.conclusionPage ? new PageConclusion(data.conclusionPage) : undefined;
        this.pages = (data.pages ?? []).map((page) => new FormPage(page));
        this.locale = data.locale;
    }

    /**
     * Ce brouillon, ses libellés de modèle remplacés par leur texte dans la langue de composition.
     *
     * Une enquête née d'un modèle arrive avec des clés de traduction partout où le modèle rédigeait
     * — titres, descriptions, choix, critères, bornes, message d'obligation. Le serveur les lui a
     * données, et les reprendrait telles quelles au premier enregistrement : il ne connaîtrait
     * alors du questionnaire qu'une suite de clés, illisible dans un export.
     *
     * Le parcours est aveugle au champ traversé : un élément conserve dans son index de secours
     * tout ce que l'éditeur ne modélise pas encore, libellés compris.
     *
     * Rien à traduire, et c'est cette instance qui revient : l'appelant y lit qu'il n'a pas de
     * correction à enregistrer.
     */
    public readonly withTranslatedLabels = (translate: LabelTranslator): Survey => {
        const plain = JSON.parse(JSON.stringify(this)) as Plain<Survey>;
        const translated = resolveLabelKeys(plain, translate);

        return JSON.stringify(translated) === JSON.stringify(plain) ? this : new Survey(translated);
    };

    /**
     * Un indicateur ne se mesure qu'une fois par enquête : deux recommandations, ou deux
     * satisfactions globales, ne se compareraient pas. Le niveau d'effort échappe à la règle dans
     * une enquête par étapes, où il se mesure à chaque étape, et un formulaire n'y est pas soumis
     * du tout : il ne mesure rien.
     */
    public readonly acceptsQuestionType = (questionType: QUESTION_TYPE, stages: Stage[] = []): boolean => {
        if (this.type === SurveyType.FORM || !UNIQUE_INDICATORS.includes(questionType)) return true;

        // Le niveau d'effort se mesure une fois par étape : l'enquête par étapes en accepte autant
        // qu'elle a d'étapes libres, et plus aucun une fois toutes prises — la question posée alors
        // n'aurait rien à mesurer, et retiendrait la diffusion sans qu'on puisse y remédier.
        if (this.type === SurveyType.SATISFACTION_STAGES && questionType === QUESTION_TYPE.CES) {
            return this.stagesFreeOfEffort(stages).length > 0;
        }

        return !this.pages.some((page) => page.elements.some((element) => element.type === questionType));
    };

    /**
     * Nombre de questions posées dans tout le questionnaire, page de fin comprise.
     *
     * Une enquête sans question ne mesure rien : c'est ce compte qui empêche de retirer la dernière.
     * Les éléments qui n'attendent pas de réponse — zone de texte, invitation à déposer un avis — ne
     * comptent pas : une enquête qui n'en porterait que ne demanderait rien.
     */
    public readonly questionCount = (): number => this.questions().length;

    /**
     * L'enquête a-t-elle de quoi être diffusée ?
     *
     * Une enquête de satisfaction, ordinaire ou par étapes, doit mesurer quelque chose : sans l'un
     * des indicateurs qu'elle attend, elle ne rapporterait rien. Un formulaire ne mesure rien, il
     * recueille : n'importe quelle question l'y autorise, sauf le commentaire seul — une enquête
     * qui ne demanderait qu'un texte libre n'aurait rien à restituer.
     *
     * Elle doit aussi laisser la parole : une note dit combien, jamais pourquoi, et c'est le
     * commentaire qui l'explique. Sans lui, l'enquête rapporterait des chiffres qu'on ne saurait pas
     * lire — d'où une question de commentaire exigée au même titre que l'indicateur.
     *
     * Une enquête par étapes ajoute sa condition : celles de ses questions qui se mesurent étape par
     * étape doivent toutes dire de laquelle elles parlent. Une seule laissée sans étape suffit à
     * retenir la diffusion, l'indicateur fût-il présent.
     */
    public readonly canPublish = (): boolean => this.publicationBlockers().length === 0;

    /**
     * Ce qui manque encore à l'enquête pour être diffusée, sous forme de clés de traduction, dans
     * l'ordre où on les lit — ce qu'elle mesure, puis ce qu'elle laisse dire, puis ses étapes.
     *
     * Une liste plutôt qu'un refus sec : l'utilisateur à qui l'on refuse la diffusion doit pouvoir
     * lire ce qu'il lui reste à faire, et tout d'un coup plutôt qu'une exigence à la fois. Vide,
     * l'enquête est diffusable — c'est ce que `canPublish` en retient.
     */
    public readonly publicationBlockers = (): string[] => {
        const blockers = this.type === SurveyType.FORM ? this.formBlockers() : this.satisfactionBlockers();

        // Vaut pour toutes les natures d'enquête : une page qui ne pose aucune question se traverse
        // sans que rien ne soit demandé. L'éditeur laisse retirer la dernière question d'une page —
        // c'est ici que la diffusion s'en trouve retenue, jusqu'à ce qu'elle en repose une.
        if (this.hasQuestionlessPage()) blockers.push('label.publication_requires_question_per_page');

        // Vaut pour toutes les natures d'enquête : ce qu'une condition à moitié composée règle ne
        // s'afficherait jamais, et le parcours s'arrêterait devant.
        if (this.hasIncompleteDisplayOptions()) blockers.push('label.publication_requires_complete_conditions');

        return blockers;
    };

    /**
     * Étapes qu'une question peut désigner, parmi celles que l'enquête définit.
     *
     * Le niveau d'effort se mesure une fois par étape : celles qu'un autre effort occupe déjà ne
     * sont plus offertes — deux mesures de la même étape ne se compareraient pas. L'étape que la
     * question tient elle-même reste offerte, sans quoi son propre réglage disparaîtrait de la liste
     * qui le montre.
     *
     * Les autres questions ne s'excluent pas entre elles : plusieurs peuvent rendre compte d'une
     * même étape, chacune sous son angle.
     */
    public readonly stagesAvailableFor = (stages: Stage[], question: ElementPage): Stage[] =>
        question.isCes() ? this.stagesFreeOfEffort(stages, question) : stages;

    /**
     * Étapes auxquelles toutes les questions d'une page peuvent être affectées d'un coup.
     *
     * Une page qui pose un niveau d'effort ne se voit offrir que les étapes qu'aucun niveau d'effort
     * du questionnaire n'occupe — le sien compris : l'effort ne se mesure qu'une fois par étape.
     */
    public readonly stagesAvailableForPage = (stages: Stage[], page: FormPage): Stage[] =>
        page.hasCes() ? this.stagesFreeOfEffort(stages) : stages;

    /**
     * Étapes qu'aucun niveau d'effort n'occupe, celui qu'on excepte mis à part.
     *
     * L'exception sert à la question qu'on règle : l'étape qu'elle tient elle-même doit rester
     * offerte, sans quoi son propre réglage disparaîtrait de la liste qui le montre. Sans exception,
     * c'est ce qui reste à une question qui n'existe pas encore.
     */
    private readonly stagesFreeOfEffort = (stages: Stage[], except?: ElementPage): Stage[] => {
        const taken = new Set(
            this.questions()
                .filter((question) => question !== except && question.isCes())
                .map((question) => question.stageId)
                .filter((stageId): stageId is number => stageId !== undefined && stageId !== null)
        );

        // Une étape que le serveur n'a pas encore enregistrée ne peut pas être désignée.
        return stages.filter((stage) => stage.id !== null && !taken.has(stage.id));
    };

    /**
     * Une question qui se mesure étape par étape reste-t-elle sans étape ?
     *
     * Ses réponses ne se rattacheraient à rien dans la restitution, et l'enquête n'est pas
     * diffusable en l'état. L'éditeur le signale dès son en-tête, sans attendre qu'on ouvre la page
     * fautive. Ne vaut que d'une enquête par étapes : ailleurs, il n'y a pas d'étape à désigner.
     */
    public readonly hasUnstagedQuestions = (): boolean =>
        this.type === SurveyType.SATISFACTION_STAGES && !this.stagesAreAssigned();

    /**
     * Une page ou un élément du questionnaire porte-t-il une condition à moitié composée ?
     *
     * Ce qu'elle règle ne s'afficherait jamais : le parcours s'arrêterait devant. L'éditeur le
     * signale dès son en-tête, sans attendre qu'on ouvre la page fautive.
     */
    public readonly hasIncompleteDisplayOptions = (): boolean =>
        this.allDisplayOptions().some((displayOptions) => !displayOptions.isComplete());

    /**
     * Un choix décide-t-il quelque part de ce qui s'affiche ?
     *
     * C'est ce qui est annoncé à l'utilisateur avant qu'il supprime le choix : les conditions qui
     * l'observent disparaîtront avec lui. Tout le questionnaire est parcouru, pages comprises — une
     * page entière peut ne s'afficher que sur ce choix-là.
     */
    public readonly usesChoiceInConditions = (questionId: number, choiceId: number): boolean =>
        this.allDisplayOptions().some((displayOptions) => displayOptions.observesChoice(questionId, choiceId));

    /**
     * Cette enquête, débarrassée des conditions qui observaient un choix supprimé.
     *
     * Le nettoyage porte sur tout le questionnaire et non sur la seule question à qui le choix
     * appartenait : c'est ailleurs qu'on s'y adosse, jamais chez soi.
     */
    public readonly withoutChoiceConditions = (questionId: number, choiceId: number): Survey => {
        const withoutChoice = <T extends {displayOptions: DisplayOptions}>(target: T): T => ({
            ...target,
            displayOptions: target.displayOptions.withoutChoice(questionId, choiceId),
        });

        return new Survey({
            ...this,
            pages: this.pages.map(
                (page) =>
                    new FormPage({
                        ...withoutChoice(page),
                        elements: page.elements.map((element) => new ElementPage(withoutChoice(element))),
                    })
            ),
            conclusionPage: this.conclusionPage && {
                ...this.conclusionPage,
                elements: this.conclusionPage.elements.map((element) => new ElementPage(withoutChoice(element))),
            },
        });
    };

    /**
     * Un critère de question multicritères décide-t-il quelque part de ce qui s'affiche ?
     *
     * C'est ce qui est annoncé à l'utilisateur avant qu'il supprime le critère : les conditions qui
     * l'observent disparaîtront avec lui. Un multicritères se conditionne critère par critère — le
     * répondant les note séparément —, d'où la recherche sur l'un d'eux et non sur la question.
     */
    public readonly usesSubQuestionInConditions = (questionId: number, subQuestionId: number): boolean =>
        this.allDisplayOptions().some((displayOptions) =>
            displayOptions.observesSubQuestion(questionId, subQuestionId)
        );

    /**
     * Cette enquête, débarrassée des conditions qui observaient un critère supprimé.
     *
     * Le nettoyage porte sur tout le questionnaire et non sur la seule question à qui le critère
     * appartenait : c'est ailleurs qu'on s'y adosse, jamais chez soi.
     */
    public readonly withoutSubQuestionConditions = (questionId: number, subQuestionId: number): Survey => {
        const withoutSubQuestion = <T extends {displayOptions: DisplayOptions}>(target: T): T => ({
            ...target,
            displayOptions: target.displayOptions.withoutSubQuestion(questionId, subQuestionId),
        });

        return new Survey({
            ...this,
            pages: this.pages.map(
                (page) =>
                    new FormPage({
                        ...withoutSubQuestion(page),
                        elements: page.elements.map((element) => new ElementPage(withoutSubQuestion(element))),
                    })
            ),
            conclusionPage: this.conclusionPage && {
                ...this.conclusionPage,
                elements: this.conclusionPage.elements.map((element) => new ElementPage(withoutSubQuestion(element))),
            },
        });
    };

    /**
     * Combien de conditions d'affichage observent, dans ce questionnaire, une réponse qui n'est plus
     * donnée avant elles.
     *
     * Une telle condition ne serait jamais vraie : ce qu'elle règle ne s'afficherait plus jamais.
     *
     * Un compte plutôt qu'un oui ou non : ce qui se demande d'un déplacement, c'est s'il en rompt de
     * nouvelles, et cela se lit en comparant l'avant et l'après. Une condition déjà orpheline — la
     * question qu'elle observait a été supprimée depuis — ne doit pas faire avertir à chaque
     * déplacement du questionnaire, pour une rupture qui ne vient pas de là.
     */
    public readonly unreachableAnswerConditionCount = (): number =>
        [
            ...this.pages.flatMap((page, pageIndex) => [
                page.displayOptions.unreachableAnswerCount(this.conditionSourcesBefore(pageIndex, 0)),
                ...page.elements.map((element, position) =>
                    element.displayOptions.unreachableAnswerCount(this.conditionSourcesBefore(pageIndex, position))
                ),
            ]),
            ...(this.conclusionPage?.elements ?? []).map((element, position) =>
                element.displayOptions.unreachableAnswerCount(this.conditionSourcesBefore(this.pages.length, position))
            ),
        ].reduce((total, count) => total + count, 0);

    /**
     * Ce questionnaire, débarrassé des conditions qui observent une réponse qui n'est pas donnée
     * avant elles — c'est ce que l'avertissement promet à l'utilisateur avant qu'il confirme un
     * déplacement.
     *
     * Elles seules s'en vont, où qu'elles soient : une cible peut perdre ses conditions sans avoir
     * bougé, parce que ce qu'elle observait est passé derrière elle. Ce qui reste vrai reste.
     *
     * Ce qui reste atteignable se lit sur le questionnaire d'avant : le nettoyage ne retire que des
     * conditions, jamais des questions, ce qui précède quoi ne change donc pas en route.
     */
    public readonly withoutUnreachableAnswerConditions = (): Survey =>
        new Survey({
            ...this,
            pages: this.pages.map(
                (page, pageIndex) =>
                    new FormPage({
                        ...page,
                        displayOptions: page.displayOptions.withoutUnreachableAnswers(
                            this.conditionSourcesBefore(pageIndex, 0)
                        ),
                        elements: page.elements.map(
                            (element, position) =>
                                new ElementPage({
                                    ...element,
                                    displayOptions: element.displayOptions.withoutUnreachableAnswers(
                                        this.conditionSourcesBefore(pageIndex, position)
                                    ),
                                })
                        ),
                    })
            ),
            conclusionPage: this.conclusionPage && {
                ...this.conclusionPage,
                elements: this.conclusionPage.elements.map(
                    (element, position) =>
                        new ElementPage({
                            ...element,
                            displayOptions: element.displayOptions.withoutUnreachableAnswers(
                                this.conditionSourcesBefore(this.pages.length, position)
                            ),
                        })
                ),
            },
        });

    /** Ce qui manque à un formulaire : une question, n'importe laquelle, qui ne soit pas un commentaire. */
    private readonly formBlockers = (): string[] =>
        this.questions().some((question) => question.type !== QUESTION_TYPE.COMMENT)
            ? []
            : [MISSING_INDICATOR_HINTS[this.type]];

    /** Ce qui manque à une enquête de satisfaction, ordinaire ou par étapes. */
    private readonly satisfactionBlockers = (): string[] => {
        const blockers: string[] = [];
        if (!this.measuresIndicator()) blockers.push(MISSING_INDICATOR_HINTS[this.type]);
        if (!this.asksForComment()) blockers.push('label.publication_requires_comment');
        if (this.hasUnstagedQuestions()) blockers.push('label.publication_requires_stage');

        return blockers;
    };

    /**
     * Une page du questionnaire reste-t-elle sans question ?
     *
     * Le répondant la traverserait sans avoir rien à répondre. La page de fin n'est pas concernée :
     * elle conclut le parcours, et n'a jamais été tenue de poser quoi que ce soit.
     */
    private readonly hasQuestionlessPage = (): boolean => this.pages.some((page) => !page.hasQuestion());

    /** L'enquête mesure-t-elle l'un des indicateurs que sa nature attend ? */
    private readonly measuresIndicator = (): boolean =>
        this.questions().some((question) => PUBLISHING_INDICATORS[this.type].includes(question.type as QUESTION_TYPE));

    /** Le répondant peut-il dire pourquoi il a noté ainsi ? */
    private readonly asksForComment = (): boolean =>
        this.questions().some((question) => question.type === QUESTION_TYPE.COMMENT);

    /**
     * Chaque question qui se mesure étape par étape dit-elle de laquelle elle parle ?
     *
     * Le rattachement se lit sur la question elle-même, et non sur la page qui la porte : une même
     * page peut suivre plusieurs étapes du parcours.
     */
    private readonly stagesAreAssigned = (): boolean =>
        this.questions()
            .filter((question) => question.isStaged())
            .every((question) => question.stageId !== undefined && question.stageId !== null);

    /**
     * Les questions du questionnaire, page de fin comprise. Les éléments qui n'attendent pas de
     * réponse — zone de texte, invitation à déposer un avis — n'en sont pas.
     */
    private readonly questions = (): ElementPage[] =>
        [...this.pages.flatMap((page) => page.elements), ...(this.conclusionPage?.elements ?? [])].filter((element) =>
            element.isQuestion()
        );

    private readonly allDisplayOptions = (): DisplayOptions[] => [
        ...this.pages.flatMap((page) => [
            page.displayOptions,
            ...page.elements.map((element) => element.displayOptions),
        ]),
        ...(this.conclusionPage?.elements ?? []).map((element) => element.displayOptions),
    ];

    /**
     * Pages qu'une condition d'affichage peut viser depuis un endroit du questionnaire, chacune avec
     * les questions qu'elle offre.
     *
     * Une condition ne peut porter que sur une réponse déjà donnée : sont retenues les questions des
     * pages antérieures, et celles qui précèdent l'élément sur sa propre page. L'adosser à une
     * question à venir ne serait jamais vrai.
     *
     * Une page dont aucune question conditionnable n'a encore été posée n'est pas proposée : elle
     * n'offrirait rien à choisir ensuite. Un tableau vide signifie donc qu'il ne reste, à cet
     * endroit, que les métadonnées — c'est le cas devant la première question de l'enquête.
     *
     * `pageIndex` vaut le nombre de pages pour un élément de la page de fin, qui vient après tout.
     */
    public readonly conditionSourcesBefore = (pageIndex: number, elementIndex: number): ConditionPageOption[] =>
        this.pages
            .slice(0, pageIndex + 1)
            // Une page que le serveur n'a pas encore enregistrée ne peut pas être désignée.
            .flatMap((page, rank) =>
                page.id === null
                    ? []
                    : [
                          {
                              pageId: page.id,
                              index: rank,
                              title: page.title[LABEL_KEY] ?? '',
                              questions: page.elements
                                  .filter((_, position) => rank < pageIndex || position < elementIndex)
                                  .filter((element) => element.isConditionable())
                                  .flatMap((element) => element.conditionOptions()),
                          },
                      ]
            )
            .filter((page) => page.questions.length > 0);
}
